/**
 * 後台 — 私宴預約(場次自助管理 + 預約清單)
 *
 * 場次管理:客戶自己建活動 → 填資訊 → 傳海報 → 按「開放預約」上線
 *   - 同一天只能有一場(後端擋同 event_date 重複開放);不同天可同時多場開放
 *   - 多場開放時 LINE 邀請卡自動變 carousel、LIFF 表單先選場次
 *   - 沒有開放中的場次時,LINE「預約私宴」自動顯示「籌備中」
 * 預約清單:依場次切換;狀態流 —
 *   待確認/候補 → 確認席次(系統推席次確認通知給會員)/ 取消;已取消可恢復
 *
 * 代報(2026-10-06 Sam 定):管理者替會員或非會員來賓登記席次(電話、現場報名等),清單標「代報」
 *   - 非會員直接填姓名手機,不建會員資料(member_uid 為空,清單標「非會員」)
 *   - 任何狀態的場次(草稿 / 預約中 / 已截止)都能代報;會員在 LINE 上看不到草稿 / 已截止場次的報名入口
 *   - 席位不能超過:不夠就自動列候補;候補轉正 / 已取消恢復時席位不夠,後端回 no_seats(要加位請先調高席位上限)
 *   - LINE 會員送出後收到確認通知;線下會員(manual_ 開頭)與非會員沒有 LINE,不推播,要另行通知
 *   - 非會員填的手機若是既有會員,後端回 phone_is_member → 提示改用會員代報,避免同一人變成兩筆資料
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, StickyNote } from "lucide-react";
import {
  adminCreateBanquetEvent,
  adminCreateBanquetReservation,
  adminListBanquetReservations,
  adminUpdateBanquetEvent,
  adminUpdateBanquetReservation,
  adminUploadBanquetHero,
  listMembers,
  type AdminBanquetReservePayload,
  type BanquetEventForm,
} from "../../api/admin";
import type {
  BanquetEvent,
  BanquetEventStatus,
  BanquetReservation,
  BanquetStatus,
} from "../../api/banquet";
import type { Member } from "../../api/members";
import AdminHeader from "../../components/AdminHeader";
import { useAuthStore } from "../../store/authStore";
import { useUIStore } from "../../store/uiStore";

const STATUS_BADGE: Record<BanquetStatus, { label: string; cls: string }> = {
  pending: { label: "待確認", cls: "bg-sage-light text-charcoal" },
  confirmed: { label: "已確認", cls: "bg-primary text-white" },
  waitlist: { label: "候補", cls: "bg-coral text-white" },
  cancelled: { label: "已取消", cls: "bg-line text-muted" },
};

const EVENT_BADGE: Record<BanquetEventStatus, { label: string; cls: string }> = {
  draft: { label: "草稿", cls: "bg-line text-muted" },
  open: { label: "預約中", cls: "bg-primary text-white" },
  closed: { label: "已截止", cls: "bg-sage-light text-charcoal" },
};

const EMPTY_FORM: BanquetEventForm = {
  title: "",
  title_en: "",
  tagline: "",
  event_date: "",
  time_label: "",
  venue: "",
  venue_address: "",
  fee_label: "",
  capacity: 12,
};

/** 有沒有 LINE 可推播:member_uid 有值,且不是後台手動建的會員(manual_ 開頭,沒有 LINE) */
function hasLine(uid: string | undefined) {
  return !!uid && !uid.startsWith("manual_");
}

export default function BanquetReservations() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.role);

  const [events, setEvents] = useState<BanquetEvent[]>([]);
  const [event, setEvent] = useState<BanquetEvent | null>(null);
  const [rows, setRows] = useState<BanquetReservation[]>([]);
  const [seats, setSeats] = useState(0);
  const [waitlistSeats, setWaitlistSeats] = useState(0);
  const [capacity, setCapacity] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [proxyOpen, setProxyOpen] = useState(false);

  // 場次編輯(editingId = "new" 表示新增)
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<BanquetEventForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");

  async function load(eventId?: string) {
    try {
      const r = await adminListBanquetReservations(eventId);
      setEvents(r.events);
      setEvent(r.event);
      setRows(r.reservations);
      setSeats(r.seats);
      setWaitlistSeats(r.waitlist_seats);
      setCapacity(r.capacity);
    } catch {
      useUIStore.getState().showToast("載入失敗", "error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (role !== "admin") {
      navigate("/admin/login", { replace: true });
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, navigate]);

  // ── 場次管理 ──────────────────────────

  function startEdit(e: BanquetEvent | null) {
    if (e === null) {
      setEditingId("new");
      setForm(EMPTY_FORM);
    } else {
      setEditingId(e.event_id);
      setForm({
        title: e.title,
        title_en: e.title_en,
        tagline: e.tagline,
        event_date: e.event_date,
        time_label: e.time_label,
        venue: e.venue,
        venue_address: e.venue_address,
        fee_label: e.fee_label,
        capacity: e.capacity,
      });
    }
  }

  async function saveEvent() {
    if (!form.title?.trim()) {
      useUIStore.getState().showToast("活動名稱必填", "error");
      return;
    }
    setSaving(true);
    try {
      const r = editingId === "new"
        ? await adminCreateBanquetEvent(form)
        : await adminUpdateBanquetEvent(editingId!, form);
      if (r.status === "ok") {
        useUIStore.getState().showToast("已儲存", "success");
        setEditingId(null);
        await load(event?.event_id);
      } else {
        useUIStore.getState().showToast(`儲存失敗:${r.status}`, "error");
      }
    } catch {
      useUIStore.getState().showToast("儲存失敗", "error");
    } finally {
      setSaving(false);
    }
  }

  async function setEventStatus(e: BanquetEvent, status: BanquetEventStatus) {
    const msg =
      status === "open"
        ? `開放「${e.title}」預約?\nLINE「預約私宴」將顯示此場次。`
        : status === "closed"
          ? `截止「${e.title}」?\n已收到的預約不受影響。`
          : null;
    if (msg && !confirm(msg)) return;
    try {
      const r = await adminUpdateBanquetEvent(e.event_id, { status });
      if (r.status === "ok") {
        useUIStore.getState().showToast(status === "open" ? "已開放預約" : "已更新", "success");
        await load(e.event_id);
      } else {
        useUIStore.getState().showToast(`更新失敗:${r.status}`, "error");
      }
    } catch (err: any) {
      // 同一天已有開放中的場次 → 後端回 400 same_day_conflict
      const data = err?.response?.data;
      if (data?.status === "same_day_conflict") {
        useUIStore.getState().showToast(
          `同一天已有開放中的場次「${data.conflict_title || ""}」,請先截止該場或改日期`,
          "error",
        );
      } else {
        useUIStore.getState().showToast("更新失敗", "error");
      }
    }
  }

  async function uploadHero(e: BanquetEvent, file: File | undefined) {
    if (!file) return;
    setUploading(e.event_id);
    try {
      const r = await adminUploadBanquetHero(e.event_id, file);
      if (r.status === "ok") {
        useUIStore.getState().showToast("海報已更新", "success");
        await load(event?.event_id);
      } else {
        useUIStore.getState().showToast(`上傳失敗:${r.status}`, "error");
      }
    } catch {
      useUIStore.getState().showToast("上傳失敗(檔案太大或格式不符)", "error");
    } finally {
      setUploading("");
    }
  }

  // ── 預約清單 ──────────────────────────

  async function setStatus(r: BanquetReservation, status: BanquetStatus) {
    if (status === "confirmed") {
      // 沒有 LINE(非會員 / 線下會員)的預約不會推播,confirm 文字要講清楚
      const notifyLine = hasLine(r.member_uid)
        ? "系統將推送席次確認通知給該會員。"
        : "此預約沒有 LINE,系統不會推播,請另行通知。";
      if (!confirm(`確認「${r.name} × ${r.party_size} 位」席次?\n${notifyLine}`)) return;
    }
    if (status === "cancelled" && !confirm(`取消「${r.name}」的預約?`)) return;
    setBusyId(r.id);
    try {
      const res = await adminUpdateBanquetReservation(r.id, status);
      if (res.status === "ok") {
        useUIStore.getState().showToast("已更新", "success");
        await load(event?.event_id);
      } else {
        useUIStore.getState().showToast(`更新失敗:${res.status}`, "error");
      }
    } catch (err: any) {
      // 候補轉正 / 已取消恢復時席位不夠 → 後端回 400 no_seats
      const data = err?.response?.data;
      if (data?.status === "no_seats") {
        useUIStore.getState().showToast(
          `席位不足(剩 ${data.remaining ?? 0} 席),無法轉正或恢復;要加位請先在場次「編輯」調高席位上限`,
          "error",
        );
      } else {
        useUIStore.getState().showToast("更新失敗", "error");
      }
    } finally {
      setBusyId("");
    }
  }

  function fmtTime(iso: string) {
    if (!iso) return "—";
    const d = new Date(iso);
    return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  }

  const formField = (label: string, key: keyof BanquetEventForm, placeholder = "") => (
    <div>
      <label className="label">{label}</label>
      <input
        className="input"
        placeholder={placeholder}
        value={(form[key] as string) ?? ""}
        onChange={(ev) => setForm((f) => ({ ...f, [key]: ev.target.value }))}
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-cream pb-12">
      <AdminHeader active="banquet" />

      <div className="px-6 mt-6 max-w-4xl space-y-6">
        {/* ── 場次管理 ── */}
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-ink">場次管理</h2>
            <button onClick={() => startEdit(null)} className="btn-primary text-sm px-4 py-1.5">
              ＋ 新增活動
            </button>
          </div>
          <div className="text-xs text-muted">
            建立活動 → 填資訊 → 傳海報 → 按「開放預約」,LINE 的「預約私宴」就會顯示這場。
            同一天只能有一場;不同天可以同時多場開放(LINE 會自動變多卡輪播)。
          </div>

          {editingId !== null && (
            <div className="card space-y-3">
              <div className="font-medium text-ink">{editingId === "new" ? "新增活動" : "編輯活動"}</div>
              {formField("活動名稱 *", "title", "例:仲夏養妍品鑑沙龍")}
              <div className="grid grid-cols-2 gap-3">
                {formField("英文副標", "title_en", "例:A Salon of Eastern Elegance")}
                {formField("主題句", "tagline", "例:三盅鑑賞・極美之選")}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">日期</label>
                  <input
                    className="input"
                    type="date"
                    value={form.event_date ?? ""}
                    onChange={(ev) => setForm((f) => ({ ...f, event_date: ev.target.value }))}
                  />
                </div>
                {formField("時間", "time_label", "例:14:00 - 16:00")}
              </div>
              <div className="grid grid-cols-2 gap-3">
                {formField("地點名稱", "venue", "例:義居雅舍")}
                {formField("地址", "venue_address", "例:臺北市內湖區內湖路一段203巷28號")}
              </div>
              <div className="grid grid-cols-2 gap-3">
                {formField("費用", "fee_label", "例:$1,699 / 位")}
                <div>
                  <label className="label">席位上限</label>
                  <input
                    className="input"
                    type="number"
                    min={1}
                    value={form.capacity ?? 12}
                    onChange={(ev) => setForm((f) => ({ ...f, capacity: Number(ev.target.value) }))}
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setEditingId(null)} className="btn-secondary flex-1">取消</button>
                <button onClick={saveEvent} disabled={saving} className="btn-primary flex-1">
                  {saving ? "儲存中…" : "儲存"}
                </button>
              </div>
              {editingId === "new" && (
                <div className="text-xs text-muted">儲存後即可在列表上傳海報、按「開放預約」上線。</div>
              )}
            </div>
          )}

          <div className="space-y-3">
            {events.length === 0 && !loading && (
              <div className="card text-muted text-sm">尚無活動 — 按「新增活動」建立第一場。</div>
            )}
            {events.map((e) => {
              const badge = EVENT_BADGE[e.status];
              return (
                <div key={e.event_id} className="card space-y-2">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                      {e.hero_url ? (
                        <img src={e.hero_url} alt="" className="w-12 h-16 object-cover rounded shrink-0" />
                      ) : (
                        <div className="w-12 h-16 bg-line rounded flex items-center justify-center text-muted text-xs shrink-0">無圖</div>
                      )}
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-xs px-2 py-0.5 rounded ${badge.cls}`}>{badge.label}</span>
                          <span className="font-medium text-ink">{e.title}</span>
                        </div>
                        <div className="text-sm text-muted truncate">
                          {e.date_label} {e.time_label} · {e.venue} · {e.fee_label}
                        </div>
                        <div className="text-sm">
                          <span className="text-primary font-medium">已訂 {e.seats ?? 0}</span>
                          <span className="text-muted"> / {e.capacity} 席</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap items-center">
                    {e.status !== "open" && (
                      <button onClick={() => setEventStatus(e, "open")} className="btn-primary text-sm px-4 py-1.5">
                        開放預約
                      </button>
                    )}
                    {e.status === "open" && (
                      <button onClick={() => setEventStatus(e, "closed")} className="btn-secondary text-sm px-4 py-1.5">
                        截止預約
                      </button>
                    )}
                    <button onClick={() => startEdit(e)} className="btn-secondary text-sm px-4 py-1.5">編輯</button>
                    <label className="btn-secondary text-sm px-4 py-1.5 cursor-pointer">
                      {uploading === e.event_id ? "上傳中…" : e.hero_url ? "更換海報" : "上傳海報"}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={uploading === e.event_id}
                        onChange={(ev) => {
                          uploadHero(e, ev.target.files?.[0]);
                          ev.target.value = "";
                        }}
                      />
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* ── 預約清單 ── */}
        <section className="space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="font-bold text-ink">預約清單</h2>
            <div className="flex items-center gap-2 flex-wrap">
              {events.length > 1 && (
                <select
                  className="input w-auto text-sm"
                  value={event?.event_id || ""}
                  onChange={(ev) => load(ev.target.value)}
                >
                  {events.map((e) => (
                    <option key={e.event_id} value={e.event_id}>
                      {e.title}({EVENT_BADGE[e.status].label})
                    </option>
                  ))}
                </select>
              )}
              {event && (
                <button onClick={() => setProxyOpen(true)} className="btn-primary text-sm px-4 py-1.5">
                  ＋ 代報
                </button>
              )}
            </div>
          </div>

          {event && (
            <div className="card">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="font-bold text-ink">{event.title}</div>
                  <div className="text-sm text-muted">
                    {event.date_label} {event.time_label} · {event.venue} · {event.fee_label}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-primary">
                    {seats} <span className="text-sm text-muted font-normal">/ {capacity} 席</span>
                  </div>
                  {waitlistSeats > 0 && (
                    <div className="text-xs text-coral">候補 {waitlistSeats} 席</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-muted">載入中…</div>
          ) : !event ? (
            <div className="card text-muted text-sm">建立活動後,預約會顯示在這裡。</div>
          ) : rows.length === 0 ? (
            <div className="card text-muted text-sm">尚無預約</div>
          ) : (
            <div className="space-y-3">
              {rows.map((r) => {
                const badge = STATUS_BADGE[r.status];
                const busy = busyId === r.id;
                return (
                  <div key={r.id} className="card space-y-2">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs px-2 py-0.5 rounded ${badge.cls}`}>{badge.label}</span>
                        <span className="font-medium text-ink">{r.name}</span>
                        <span className="text-sm text-muted">{r.party_size} 位</span>
                        {/* 屬性標籤用外框、狀態用實心,一眼分得開;bg-line + text-muted 對比太低看不清 */}
                        {r.source === "admin" && (
                          <span className="text-xs px-2 py-0.5 rounded border border-line text-charcoal">代報</span>
                        )}
                        {!r.member_uid && (
                          <span className="text-xs px-2 py-0.5 rounded border border-line text-charcoal">非會員</span>
                        )}
                      </div>
                      <div className="text-xs text-muted">{fmtTime(r.created_at)}</div>
                    </div>
                    <div className="text-sm text-muted">
                      <Phone size={14} className="inline -mt-0.5 mr-1" aria-hidden="true" />{r.phone}
                      {r.note ? (
                        <span className="ml-3">
                          <StickyNote size={14} className="inline -mt-0.5 mr-1" aria-hidden="true" />{r.note}
                        </span>
                      ) : null}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {(r.status === "pending" || r.status === "waitlist") && (
                        <button
                          disabled={busy}
                          onClick={() => setStatus(r, "confirmed")}
                          className="btn-primary text-sm px-4 py-1.5"
                        >
                          {r.status === "waitlist" ? "轉正確認" : "確認席次"}
                        </button>
                      )}
                      {r.status !== "cancelled" && (
                        <button
                          disabled={busy}
                          onClick={() => setStatus(r, "cancelled")}
                          className="btn-secondary text-sm px-4 py-1.5"
                        >
                          取消
                        </button>
                      )}
                      {r.status === "cancelled" && (
                        <button
                          disabled={busy}
                          onClick={() => setStatus(r, "pending")}
                          className="btn-secondary text-sm px-4 py-1.5"
                        >
                          恢復待確認
                        </button>
                      )}
                      {/* 非會員來賓(代報)沒有會員資料可看 */}
                      {r.member_uid && (
                        <button
                          onClick={() => navigate(`/admin/members/${r.member_uid}`)}
                          className="text-sm text-primary px-2 py-1.5"
                        >
                          會員 360 ›
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {/* 代報 modal */}
      {proxyOpen && event && (
        <ProxyReserveModal
          event={event}
          seats={seats}
          capacity={capacity}
          onClose={() => setProxyOpen(false)}
          onSuccess={async () => {
            setProxyOpen(false);
            await load(event.event_id);
          }}
        />
      )}
    </div>
  );
}

// ── 代報 ──────────────────────────────────

type ProxyMode = "member" | "guest";

function ProxyReserveModal({
  event,
  seats,
  capacity,
  onClose,
  onSuccess,
}: {
  event: BanquetEvent;
  seats: number;
  capacity: number;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const remaining = Math.max(0, capacity - seats);
  const eventBadge = EVENT_BADGE[event.status];

  const [mode, setMode] = useState<ProxyMode>("member");

  // 會員模式:搜尋 + 選定。picked 只用得到 line_user_id / name / phone,
  // 所以 phone_is_member 時可以直接用後端回的 member_uid + member_name 組一個
  const [search, setSearch] = useState("");
  const [candidates, setCandidates] = useState<Member[]>([]);
  const [searchedKw, setSearchedKw] = useState(""); // candidates 對應的關鍵字(輸入到一半不要先顯示「找不到」)
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState<Member | null>(null);

  // 兩種身分共用的欄位
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [partySize, setPartySize] = useState(1);
  const [note, setNote] = useState("");
  const [regStatus, setRegStatus] = useState<"pending" | "confirmed">("pending");
  const [busy, setBusy] = useState(false);

  // 非會員模式填的手機其實是既有會員(後端 phone_is_member)→ 提示改用會員代報
  const [phoneMember, setPhoneMember] = useState<{ uid: string; name: string } | null>(null);

  useEffect(() => {
    const kw = search.trim();
    if (!kw) {
      setCandidates([]);
      setSearchedKw("");
      setSearching(false);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await listMembers({ search: kw, limit: 20 });
        if (cancelled) return;
        setCandidates(r.members.filter((mm) => (mm.status as string) !== "merged"));
        setSearchedKw(kw);
      } catch {
        // ignore — 搜尋失敗不擋
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [search]);

  function switchMode(m: ProxyMode) {
    setMode(m);
    setPhoneMember(null); // 切換時清掉錯誤提示
  }

  function pickMember(m: Member) {
    setPicked(m);
    setName(m.name || m.display_name || "");
    setPhone(m.phone || "");
  }

  // phone_is_member → 切到會員模式並直接選定該會員(手機沿用目前輸入的)
  function switchToMember() {
    if (!phoneMember) return;
    setPicked({ line_user_id: phoneMember.uid, name: phoneMember.name, phone: phone.trim() });
    if (phoneMember.name) setName(phoneMember.name);
    setMode("member");
    setPhoneMember(null);
  }

  function reportFail(data: any) {
    const toast = useUIStore.getState().showToast;
    const st: string | undefined = data?.status;
    switch (st) {
      case "already_reserved": {
        const ex = data?.reservation;
        const label = ex?.status ? STATUS_BADGE[ex.status as BanquetStatus]?.label : "";
        toast(ex?.name ? `本場已有預約:${ex.name}${label ? `(${label})` : ""}` : "本場已有預約", "error");
        break;
      }
      case "phone_is_member": {
        // toast 之外,modal 內再放提示框 + 「改用會員代報」按鈕(toast 3 秒就消失)
        toast(`這支手機是會員「${data?.member_name || "(無名)"}」,請改用會員代報`, "error");
        if (data?.member_uid) setPhoneMember({ uid: data.member_uid, name: data.member_name || "" });
        break;
      }
      case "member_not_found":
        toast("找不到這位會員", "error");
        break;
      case "missing_fields":
        toast("姓名、手機必填", "error");
        break;
      case "invalid_phone":
        toast("手機格式不正確", "error");
        break;
      case "invalid_party_size":
        toast("人數需為 1-4 位", "error");
        break;
      case "event_not_found":
        toast("找不到這個場次", "error");
        break;
      default:
        toast(st ? `代報失敗:${st}` : "代報失敗", "error");
    }
  }

  async function submit() {
    if (mode === "member" && !picked) {
      useUIStore.getState().showToast("請先選擇會員", "error");
      return;
    }
    const n = name.trim();
    const p = phone.trim();
    if (!n || !p) {
      useUIStore.getState().showToast("姓名、手機必填", "error");
      return;
    }
    const payload: AdminBanquetReservePayload = {
      event_id: event.event_id,
      name: n,
      phone: p,
      party_size: partySize,
      status: regStatus,
    };
    if (mode === "member" && picked) payload.member_uid = picked.line_user_id; // 非會員不帶
    if (note.trim()) payload.note = note.trim();

    setBusy(true);
    setPhoneMember(null);
    try {
      const r = await adminCreateBanquetReservation(payload);
      if (r.status === "ok") {
        // 席位不夠 → 後端自動改候補(waitlist=true)
        useUIStore.getState().showToast(r.waitlist ? "席位已滿,已列入候補" : "已代報", "success");
        onSuccess();
      } else {
        reportFail(r);
      }
    } catch (err: any) {
      reportFail(err?.response?.data);
    } finally {
      setBusy(false);
    }
  }

  const modeBtnCls = (m: ProxyMode) =>
    `${mode === m ? "btn-primary" : "btn-secondary"} flex-1 text-sm py-2`;

  // 會員模式要選定後才顯示姓名 / 手機;非會員模式一開始就顯示
  const showFields = mode === "guest" || picked !== null;

  const notifyHint =
    mode === "guest"
      ? "非會員沒有 LINE,系統不會推播,請另行通知。"
      : !picked
        ? ""
        : hasLine(picked.line_user_id)
          ? "送出後系統會推送確認通知給會員。"
          : "此會員沒有 LINE,系統不會推播,請另行通知。";

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="space-y-1">
          <h2 className="text-lg font-bold">代報</h2>
          <div className="flex items-center gap-2 flex-wrap text-sm">
            <span className={`text-xs px-2 py-0.5 rounded ${eventBadge.cls}`}>{eventBadge.label}</span>
            <span className="text-ink font-medium">{event.title}</span>
          </div>
          <div className="text-sm text-muted">
            目前 {seats} / {capacity} 席,剩 {remaining} 席
          </div>
        </div>

        {(event.status === "draft" || event.status === "closed") && (
          <div className="text-xs text-muted">
            此場次{event.status === "draft" ? "尚未開放" : "已截止"},會員在 LINE 上看不到報名入口;代報仍可登記。
          </div>
        )}

        {/* 身分切換 */}
        <div className="flex gap-2">
          <button
            onClick={() => switchMode("member")}
            disabled={busy}
            aria-pressed={mode === "member"}
            className={modeBtnCls("member")}
          >
            會員
          </button>
          <button
            onClick={() => switchMode("guest")}
            disabled={busy}
            aria-pressed={mode === "guest"}
            className={modeBtnCls("guest")}
          >
            非會員來賓
          </button>
        </div>

        {/* 會員模式:搜尋 / 已選 */}
        {mode === "member" &&
          (picked ? (
            <div className="flex items-center justify-between gap-3 bg-cream rounded-lg p-3">
              <div className="min-w-0">
                <div className="font-medium">{picked.name || picked.display_name || "(無名)"}</div>
                <div className="text-xs text-muted mt-0.5">
                  {picked.phone || "無電話"} · {hasLine(picked.line_user_id) ? "LINE 會員" : "線下會員"}
                </div>
              </div>
              <button
                onClick={() => setPicked(null)}
                disabled={busy}
                className="text-xs text-coral hover:underline shrink-0"
              >
                更換
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <div>
                <label className="label">搜尋會員(姓名 / 電話 / Email)</label>
                <input
                  className="input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="輸入關鍵字搜尋"
                  autoFocus
                />
              </div>

              {searching && <div className="text-xs text-muted">搜尋中…</div>}

              {candidates.length > 0 && (
                <div className="border border-line rounded-lg divide-y divide-line max-h-60 overflow-y-auto">
                  {candidates.map((c) => (
                    <button
                      key={c.line_user_id}
                      onClick={() => pickMember(c)}
                      className="w-full text-left px-3 py-2 hover:bg-cream text-sm"
                    >
                      <div className="font-medium">{c.name || c.display_name || "(無名)"}</div>
                      <div className="text-xs text-muted">
                        {c.phone || "無電話"} · {hasLine(c.line_user_id) ? "LINE 會員" : "線下會員"}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {!searching && searchedKw !== "" && searchedKw === search.trim() && candidates.length === 0 && (
                <div className="text-sm text-muted">找不到符合的會員</div>
              )}
            </div>
          ))}

        {/* 姓名 / 手機(會員模式為選定後帶入、可改) */}
        {showFields && (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">姓名 *</label>
                <input
                  className="input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="例:王小明"
                />
              </div>
              <div>
                <label className="label">手機 *</label>
                <input
                  className="input"
                  type="tel"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    setPhoneMember(null); // 手機改了,上一個「這支手機是會員」提示作廢
                  }}
                  placeholder="例:0912345678"
                />
              </div>
            </div>
            {mode === "guest" && (
              <div className="text-xs text-muted">
                非會員只登記姓名與手機,不會建立會員資料;之後參加活動再引導加入會員。
              </div>
            )}
          </div>
        )}

        {mode === "guest" && phoneMember && (
          <div className="border border-coral/40 bg-coral/5 rounded-lg p-3 space-y-2">
            <div className="text-sm font-medium text-coral">
              這支手機是會員「{phoneMember.name || "(無名)"}」,請改用會員代報
            </div>
            <button onClick={switchToMember} className="btn-secondary text-sm px-4 py-1.5">
              改用會員代報
            </button>
          </div>
        )}

        {/* 人數 / 備註 */}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="label">人數</label>
            <select
              className="input"
              value={partySize}
              onChange={(e) => setPartySize(Number(e.target.value))}
            >
              {[1, 2, 3, 4].map((n) => (
                <option key={n} value={n}>{n} 位</option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <label className="label">備註</label>
            <input
              className="input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="例:一位吃素"
            />
          </div>
        </div>

        {/* 登記狀態 */}
        <div>
          <div className="label">登記狀態</div>
          <div className="flex gap-5 text-sm">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="proxy_status"
                checked={regStatus === "pending"}
                onChange={() => setRegStatus("pending")}
              />
              待確認
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="proxy_status"
                checked={regStatus === "confirmed"}
                onChange={() => setRegStatus("confirmed")}
              />
              直接確認席次
            </label>
          </div>
        </div>

        {partySize > remaining && (
          <div className="text-sm text-coral">
            剩餘席位不足(剩 {remaining} 席),送出後會列入候補。
          </div>
        )}

        {notifyHint && <div className="text-xs text-muted">{notifyHint}</div>}

        <div className="flex gap-3 pt-1">
          <button onClick={onClose} className="btn-secondary flex-1" disabled={busy}>
            取消
          </button>
          <button onClick={submit} className="btn-primary flex-1" disabled={busy}>
            {busy ? "送出中…" : "確認代報"}
          </button>
        </div>
      </div>
    </div>
  );
}
